/**
 * useStudioHostServices — wire the renderer-v2 host layer to studio's real
 * build-mode canvas, so the four host-effect ActionFlow actions stop being
 * silent no-ops in the actual preview (previously only the /__host-wiring smoke
 * route wired them).
 *
 * Returns a stable `{ hostServices, modalRegistry }` to hand to
 * `<AppV2 hostServices={…} modalRegistry={…} />`. Passing `hostServices` makes
 * AppRuntime wire the engine's four hooks (block-scoped callback/media resolve
 * via the dispatching block's namespace); passing `modalRegistry` mounts the
 * built-in `<ModalHost>`.
 *
 *   - openModal      → modalRegistry (generic HostModal) — a real dialog opens.
 *   - invokeCallback → global-fallback handlers for the corpus callback names,
 *                      surfaced as a preview toast (no real backend in preview).
 *   - emitEvent      → live AppEventBus (fans out to named subscribers; already
 *                      visible in the ActionFlow trace overlay). The bus is
 *                      name-keyed, so genuine cross-block reactions are wired by
 *                      subscribing a specific event name on a studio surface.
 *   - mediaControl   → the registry exists, but this bundle does NOT declare
 *                      host.media/control without a concrete media element
 *                      registration for the materialized application.
 *   - cropImage      → already wired in the renderer-v2 platform adapter.
 *
 * See docs/studio/actionflow-host-wiring.md.
 */

import { useMemo } from 'react'
import { createHostServices, type HostServices, type ModalComponentRegistry } from '@cogo/react-renderer-v2'
import {
  hashHostCapabilityContent,
  type HostRegistryHandlerDeclaration,
} from '@cogo/schema/host-capability-manifest'
import type { ContentRef } from '@cogo/publisher-core'
import { toast } from '@/store/toast-store'
import {
  createStudioModalRegistryBundle,
  type StudioModalRegistryBundle,
} from './host-modal'

export interface StudioHostServices {
  hostServices: HostServices
  modalRegistry: ModalComponentRegistry
  capabilityBindings: readonly StudioHostCapabilityBinding[]
  registryHandlers: readonly HostRegistryHandlerDeclaration[]
  wiredRegistryHandlers: readonly Pick<
    HostRegistryHandlerDeclaration,
    'capabilityId' | 'selector' | 'handlerRef'
  >[]
}

export interface StudioHostCapabilityBinding {
  capabilityId: `host.${string}`
  operation: string
  adapterRef: ContentRef
  sideEffectClass: 'none' | 'reversible' | 'external'
  implementation: (...args: never[]) => unknown
}

const VERIFIED_STUDIO_REGISTRY_HANDLERS = new WeakSet<object>()

export function isStudioWiredRegistryHandler(value: object): boolean {
  return VERIFIED_STUDIO_REGISTRY_HANDLERS.has(value)
}

/**
 * Default preview handlers for the corpus `invokeCallback` names. These are
 * global fallbacks (blockId null → matches any block); a studio surface can
 * override one by registering a block-scoped handler of the same name.
 */
const PREVIEW_CALLBACKS: Record<string, { message: string; type: 'success' | 'info' }> = {
  onApprove: { message: 'Approved', type: 'success' },
  onReject: { message: 'Rejected', type: 'info' },
  onConfirm: { message: 'Confirmed', type: 'success' },
  onCancel: { message: 'Cancelled', type: 'info' },
}

const STUDIO_HOST_SERVICES_DESCRIPTOR = {
  id: 'cogo.studio.host-services.v1',
  implementation: 'apps/cogo-studio/src/shell/ide-v2/host/use-studio-host-services.ts',
  operations: {
    callback: ['invoke'],
    eventBus: ['emit'],
    motion: ['run'],
  },
  callbacks: Object.keys(PREVIEW_CALLBACKS).sort(),
} as const

const STUDIO_HOST_SERVICES_REF = {
  ref: 'cogo://studio/host-adapter/host-services/v1',
  contentHash: hashHostCapabilityContent(STUDIO_HOST_SERVICES_DESCRIPTOR),
} as const

function callbackHandlerRef(name: string) {
  return {
    ref: `cogo://studio/host-handler/callback/${name}/v1`,
    contentHash: hashHostCapabilityContent({
      ...STUDIO_HOST_SERVICES_DESCRIPTOR,
      callback: name,
      presentation: PREVIEW_CALLBACKS[name],
    }),
  } as const
}

export function createStudioHostServices(
  modalBundle: StudioModalRegistryBundle = createStudioModalRegistryBundle(),
): StudioHostServices {
  const services = createHostServices()
  const motionImplementation = (
    services as HostServices & {
      motionRegistry?: { run?: (...args: never[]) => unknown }
    }
  ).motionRegistry?.run
  const callbackHandlers = Object.entries(PREVIEW_CALLBACKS)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, presentation]) => {
      const handler = () => toast[presentation.type](presentation.message)
      services.callbackRegistry.register(null, name, handler)
      const handlerRef = callbackHandlerRef(name)
      return {
        declaration: {
          capabilityId: 'host.callback' as const,
          selector: name,
          payloadSchema: { kind: 'unknown' as const },
          handlerRef,
        },
        wired: {
          capabilityId: 'host.callback' as const,
          selector: name,
          handlerRef,
        },
      }
    })
  const wiredRegistryHandlers = [
    ...callbackHandlers.map(({ wired }) => wired),
    ...modalBundle.wiredRegistryHandlers,
  ]
  for (const row of wiredRegistryHandlers) {
    VERIFIED_STUDIO_REGISTRY_HANDLERS.add(row)
  }
  return {
    hostServices: services,
    modalRegistry: modalBundle.modalRegistry,
    capabilityBindings: [
      {
        capabilityId: 'host.callback',
        operation: 'invoke',
        adapterRef: STUDIO_HOST_SERVICES_REF,
        sideEffectClass: 'external',
        implementation: services.callbackRegistry.invoke as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.event-bus',
        operation: 'emit',
        adapterRef: STUDIO_HOST_SERVICES_REF,
        sideEffectClass: 'reversible',
        implementation: services.eventBus.emit as (...args: never[]) => unknown,
      },
      {
        capabilityId: 'host.modal',
        operation: 'open',
        adapterRef: modalBundle.adapterRef,
        sideEffectClass: 'reversible',
        implementation: services.modalStore.open as (...args: never[]) => unknown,
      },
      ...(typeof motionImplementation === 'function'
        ? [
            {
              capabilityId: 'host.motion' as const,
              operation: 'run',
              adapterRef: STUDIO_HOST_SERVICES_REF,
              sideEffectClass: 'reversible' as const,
              implementation: motionImplementation,
            },
          ]
        : []),
    ],
    registryHandlers: [
      ...callbackHandlers.map(({ declaration }) => declaration),
      ...modalBundle.registryHandlers,
    ],
    wiredRegistryHandlers,
  }
}

export function useStudioHostServices(): StudioHostServices {
  // Register the preview callback fallbacks during bundle creation
  // (synchronously, in useMemo) — NOT in a useEffect. AppV2 is a descendant, so
  // its mount effect (onReady → runInitFlows) runs before any ancestor passive
  // effect here; a mount-time `invokeCallback` would otherwise be dropped before
  // the handlers were registered. The registry is owned by this same bundle, so
  // this is local initialization, not an external render side-effect.
  return useMemo(() => createStudioHostServices(), [])
}
